import React, { useState, useEffect } from 'react';
import adminApi from '../../utils/adminApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Mail, Phone, Building2, User, MessageSquare, Trash2, RefreshCw,
  Loader2, Search, Eye, CheckCircle2, Clock, Circle, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const SERVICE_LABELS = {
  ocean: 'Ocean Freight (LCL/FCL)',
  sourcing: 'China Sourcing',
  customs: 'Customs Clearance',
  other: 'Other Services'
};

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-blue-500', icon: Circle },
  contacted: { label: 'Contacted', color: 'bg-yellow-500', icon: Clock },
  closed: { label: 'Closed', color: 'bg-green-500', icon: CheckCircle2 }
};

const InquiriesTab = () => {
  const [inquiries, setInquiries] = useState([]);
  const [counts, setCounts] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchInquiries();
  }, [statusFilter]);

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status_filter: statusFilter } : {};
      const response = await adminApi.get('/api/inquiries', { params });
      if (response.data.success) {
        setInquiries(response.data.inquiries || []);
        setCounts(response.data.counts || {});
      }
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error('Failed to fetch inquiries');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (inquiryId, newStatus) => {
    try {
      await adminApi.patch(`/api/inquiries/${inquiryId}`, { status: newStatus });
      toast.success(`Marked as ${STATUS_CONFIG[newStatus].label}`);
      fetchInquiries();
      if (selectedInquiry?.id === inquiryId) {
        setSelectedInquiry({ ...selectedInquiry, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await adminApi.delete(`/api/inquiries/${deleteConfirm.id}`);
      toast.success('Inquiry deleted');
      setDeleteConfirm(null);
      setSelectedInquiry(null);
      fetchInquiries();
    } catch (error) {
      toast.error('Failed to delete inquiry');
    }
  };

  const filteredInquiries = inquiries.filter(i => {
    const query = searchQuery.toLowerCase();
    return (
      i.name?.toLowerCase().includes(query) ||
      i.email?.toLowerCase().includes(query) ||
      i.company?.toLowerCase().includes(query) ||
      i.phone?.includes(query)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatsCard label="Total" value={counts.total} color="bg-gradient-to-br from-[#003d5c] to-[#005580]" />
        <StatsCard label="New" value={counts.new} color="bg-gradient-to-br from-blue-500 to-blue-600" />
        <StatsCard label="Contacted" value={counts.contacted} color="bg-gradient-to-br from-yellow-500 to-yellow-600" />
        <StatsCard label="Closed" value={counts.closed} color="bg-gradient-to-br from-green-500 to-green-600" />
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, company, or phone..."
            className="pl-12 h-12"
            data-testid="inquiries-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-48 h-12" data-testid="status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={fetchInquiries} variant="outline" disabled={loading} className="h-12">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Inquiries List */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-[#003d5c] mx-auto" />
          </div>
        ) : filteredInquiries.length === 0 ? (
          <div className="p-12 text-center">
            <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No inquiries found</p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredInquiries.map((inquiry) => {
              const statusInfo = STATUS_CONFIG[inquiry.status] || STATUS_CONFIG.new;
              const StatusIcon = statusInfo.icon;
              return (
                <div key={inquiry.id} className="p-5 hover:bg-blue-50/50 transition-colors">
                  <div className="flex flex-col md:flex-row justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-white text-xs font-semibold ${statusInfo.color}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusInfo.label}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(inquiry.created_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="text-lg font-bold text-[#003d5c]">{inquiry.name}</h3>
                        <span className="text-gray-500">•</span>
                        <span className="text-gray-600 font-medium">{inquiry.company}</span>
                      </div>
                      <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                        <a href={`mailto:${inquiry.email}`} className="flex items-center gap-1 hover:text-[#003d5c]">
                          <Mail className="h-3 w-3" />{inquiry.email}
                        </a>
                        <a href={`tel:${inquiry.phone}`} className="flex items-center gap-1 hover:text-[#003d5c]">
                          <Phone className="h-3 w-3" />{inquiry.phone}
                        </a>
                        <span className="text-blue-700 font-medium">
                          {SERVICE_LABELS[inquiry.service] || inquiry.service}
                        </span>
                      </div>
                      <p className="mt-3 text-gray-700 text-sm line-clamp-2">
                        {inquiry.message}
                      </p>
                    </div>
                    <div className="flex md:flex-col gap-2">
                      <Button
                        onClick={() => setSelectedInquiry(inquiry)}
                        size="sm"
                        variant="outline"
                        data-testid={`view-inquiry-${inquiry.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button
                        onClick={() => setDeleteConfirm(inquiry)}
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Inquiry Detail Dialog */}
      <Dialog open={!!selectedInquiry} onOpenChange={() => setSelectedInquiry(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#003d5c]">Inquiry Details</DialogTitle>
            <DialogDescription>
              Received {selectedInquiry && formatDate(selectedInquiry.created_at)}
            </DialogDescription>
          </DialogHeader>

          {selectedInquiry && (
            <div className="space-y-4">
              {/* Status Actions */}
              <div className="flex gap-2 items-center">
                <span className="text-sm text-gray-600 font-medium">Status:</span>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                  const Icon = config.icon;
                  const active = selectedInquiry.status === key;
                  return (
                    <Button
                      key={key}
                      onClick={() => updateStatus(selectedInquiry.id, key)}
                      size="sm"
                      className={`${active ? config.color + ' text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
                    >
                      <Icon className="h-3 w-3 mr-1" />
                      {config.label}
                    </Button>
                  );
                })}
              </div>

              {/* Details Grid */}
              <div className="grid md:grid-cols-2 gap-3">
                <DetailField icon={User} label="Name" value={selectedInquiry.name} />
                <DetailField icon={Building2} label="Company" value={selectedInquiry.company} />
                <DetailField
                  icon={Mail}
                  label="Email"
                  value={
                    <a href={`mailto:${selectedInquiry.email}`} className="text-blue-600 hover:underline">
                      {selectedInquiry.email}
                    </a>
                  }
                />
                <DetailField
                  icon={Phone}
                  label="Phone"
                  value={
                    <a href={`tel:${selectedInquiry.phone}`} className="text-blue-600 hover:underline">
                      {selectedInquiry.phone}
                    </a>
                  }
                />
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <p className="text-xs text-gray-600 uppercase tracking-wide font-semibold mb-1">Service Requested</p>
                <p className="text-[#003d5c] font-bold">
                  {SERVICE_LABELS[selectedInquiry.service] || selectedInquiry.service}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-600 uppercase tracking-wide font-semibold mb-2">Message</p>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 whitespace-pre-wrap text-gray-800">
                  {selectedInquiry.message}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2 pt-2 border-t">
                <a
                  href={`mailto:${selectedInquiry.email}?subject=Re: Your COZEY YILDIZ Inquiry`}
                  className="flex-1"
                >
                  <Button className="w-full bg-[#003d5c] hover:bg-[#002a40]">
                    <Mail className="h-4 w-4 mr-2" />
                    Reply via Email
                  </Button>
                </a>
                <a
                  href={`https://wa.me/${selectedInquiry.phone.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <Button className="w-full bg-green-500 hover:bg-green-600">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Contact via WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Inquiry?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the inquiry from <strong>{deleteConfirm?.name}</strong>?
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const StatsCard = ({ label, value, color }) => (
  <div className={`${color} text-white p-5 rounded-xl shadow-lg`}>
    <p className="text-sm opacity-90">{label}</p>
    <p className="text-3xl font-bold mt-1">{value || 0}</p>
  </div>
);

const DetailField = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
    <div className="w-8 h-8 rounded bg-[#003d5c] flex items-center justify-center flex-shrink-0">
      <Icon className="h-4 w-4 text-white" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="font-semibold text-[#003d5c] truncate">{value}</p>
    </div>
  </div>
);

export default InquiriesTab;
